import React from "react";
import { useTranslate } from "@refinedev/core";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Layout,
  Result,
  Row,
  Spin,
  notification,
  theme,
} from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import { axiosInstance } from "../../authProvider";
import { isCaptchaEnabled } from "../../shared/captcha";
import { PageTitle } from "../login/components/PageTitle";
import { AuthHeroPanel } from "../login/components/AuthHeroPanel";

const { useToken } = theme;

/**
 * Public self-serve signup: creates a new tenant plus its first admin user.
 * The tenant stays suspended until the emailed activation link is opened.
 */
const TenantSignup = () => {
  const _t = useTranslate();
  const { token } = useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [submittedEmail, setSubmittedEmail] = React.useState<string>("");
  const [notif, contextHolder] = notification.useNotification();
  const [recaptchaValue, setRecaptchaValue] = React.useState<string | null>(null);
  const recaptchaRef = React.useRef<any>(null);

  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaValue(null);
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      await axiosInstance.post("/tenant-signup", {
        companyName: values.companyName,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        recaptchaToken: recaptchaValue ?? undefined,
      });
      setSubmittedEmail(values.email);
      setSuccess(true);
    } catch (error: any) {
      const errors = error?.response?.data?.errors ?? {};

      if (errors.email === "emailAlreadyExists") {
        form.setFields([
          {
            name: "email",
            errors: [_t("pages.tenantSignup.errors.emailAlreadyExists")],
          },
        ]);
      } else if (errors.recaptcha === "captchaFailed") {
        resetCaptcha();
        notif.error({
          message: _t("pages.register.errors.title"),
          description: _t("pages.tenantSignup.errors.captchaFailed"),
        });
      } else if (errors.signup === "signupDisabled") {
        notif.error({
          message: _t("pages.register.errors.title"),
          description: _t("pages.tenantSignup.errors.disabled"),
        });
      } else {
        notif.error({
          message: _t("pages.register.errors.title"),
          description: _t("pages.register.errors.unknown"),
          duration: 0,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Row
        justify="center"
        align="middle"
        style={{ minHeight: "100vh", backgroundColor: token.colorBgLayout }}
      >
        <Col className="sm:w-100 md:w-1/2">
          <Card
            className="mt-16 text-center"
            style={{
              backgroundColor: token.colorBgContainer,
              borderColor: token.colorBorder,
            }}
          >
            <Result
              status="success"
              title={_t("pages.tenantSignup.successMessage")}
              subTitle={_t("pages.tenantSignup.successSubMessage")}
              extra={
                <Link
                  to={`/resend-activation?email=${encodeURIComponent(submittedEmail)}`}
                >
                  {_t("pages.tenantSignup.notReceived")}
                </Link>
              }
            />
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {contextHolder}
      <Layout style={{ backgroundColor: token.colorBgLayout }}>
        <Row style={{ minHeight: "100dvh" }}>
          <Col
            xs={24}
            lg={10}
            className="overflow-y-auto"
            style={{
              padding: "16px",
              paddingTop: "48px",
              paddingLeft: "32px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              backgroundColor: token.colorBgContainer,
            }}
          >
            <div className="w-full py-4" style={{ maxWidth: 600 }}>
              <PageTitle />
              <Card
                className="mt-8"
                style={{
                  backgroundColor: token.colorBgContainer,
                  borderColor: token.colorBorder,
                  color: token.colorText,
                }}
              >
                <Form
                  form={form}
                  name="tenant-signup"
                  layout="vertical"
                  onFinish={onFinish}
                  autoComplete="off"
                >
                  <Row gutter={16}>
                    <Col span={24}>
                      <h3 className="text-center">{_t("pages.tenantSignup.helpMessage")}</h3>
                    </Col>
                    <Col span={24} className="mt-4">
                      <Form.Item
                        name="companyName"
                        label={_t("pages.tenantSignup.fields.companyName")}
                        rules={[
                          {
                            required: true,
                            message: _t("pages.tenantSignup.errors.required"),
                          },
                        ]}
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="firstName"
                        label={_t("users.fields.firstName")}
                        rules={[
                          {
                            required: true,
                            message: _t("pages.tenantSignup.errors.required"),
                          },
                        ]}
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="lastName"
                        label={_t("users.fields.lastName")}
                        rules={[
                          {
                            required: true,
                            message: _t("pages.tenantSignup.errors.required"),
                          },
                        ]}
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        name="email"
                        label={_t("users.fields.email")}
                        rules={[
                          {
                            required: true,
                            message: _t("pages.tenantSignup.errors.required"),
                          },
                          {
                            type: "email",
                            message: _t("pages.register.errors.invalidEmail"),
                          },
                        ]}
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="password"
                        label={_t("pages.register.fields.password")}
                        tooltip={_t("pages.register.fields.passwordTooltip")}
                        rules={[
                          {
                            required: true,
                            message: _t("pages.tenantSignup.errors.required"),
                          },
                          {
                            min: 8,
                            message: _t("pages.register.errors.passwordMinLength"),
                          },
                          {
                            pattern: new RegExp(
                              "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])",
                            ),
                            message: _t("pages.register.errors.passwordComplexity"),
                          },
                        ]}
                      >
                        <Input size="large" type="password" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="confirmPassword"
                        dependencies={["password"]}
                        label={_t("pages.register.fields.confirmPassword")}
                        rules={[
                          {
                            required: true,
                            message: _t("pages.tenantSignup.errors.required"),
                          },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue("password") === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(
                                _t("pages.register.errors.passwordMismatch"),
                              );
                            },
                          }),
                        ]}
                      >
                        <Input size="large" type="password" />
                      </Form.Item>
                    </Col>
                    {isCaptchaEnabled && (
                      <Col span={24}>
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                          onChange={setRecaptchaValue}
                        />
                      </Col>
                    )}
                  </Row>

                  <Row gutter={16} className="pt-4">
                    <Col span={24}>
                      <Form.Item>
                        <Spin spinning={loading}>
                          <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            block
                            disabled={isCaptchaEnabled && !recaptchaValue}
                          >
                            {_t("pages.tenantSignup.buttons.submit")}
                          </Button>
                        </Spin>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col span={24} className="text-center">
                      <Link to="/login">
                        {_t("pages.tenantSignup.buttons.haveAccount")}
                      </Link>
                    </Col>
                  </Row>
                </Form>
              </Card>
            </div>
          </Col>
          <Col xs={0} lg={14} className="relative min-h-full p-0">
            <AuthHeroPanel />
          </Col>
        </Row>
      </Layout>
    </>
  );
};

export default TenantSignup;
